import pluralize from "pluralize";
import { type FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { HeaderDivider } from "widgets";

import { isSyntenySet } from "@/app-utils/entity.ts";
import { type GenomeStatistics, type Organism } from "@/graphql.ts";
import { RESET_GENOME } from "@/redux/action-types.ts";
import { type IGenomeState, shouldLoadGenome } from "@/redux/modules/genome.ts";
import { createTableSpecForOrganismParent, triggerTableRefresh } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, SEQUENCING_TYPE } from "@/settings/settings.js";
import { CoordinateListUploadButton } from "@/ui-modules/entities/CoordinatesListUpload.tsx";
import { GenomeSyntenyView } from "@/ui-modules/entities/GenomeSyntenyView.tsx";
import { GenomeNucleotideFeatureSelector } from "@/ui-modules/entities/NucleotideFeatureSelector.tsx";
import { GenomeRelatedGenomicFeaturesView } from "@/ui-modules/entities/RelatedGenomicFeaturesView.tsx";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import { GenomeGenomeView } from "./GenomeGenomeView.js";
import GenomeRelatedEntitiesView from "./GenomeRelatedEntitiesView.js";
import { GenomeSummaryView } from "./GenomeSummaryView.js";
import GenomeVariantsView from "./GenomeVariantsView.js";

const GENOMIC_FEATURE_SYNTHETIC_PROPERTY = "genomicFeatures";

/**
 * The GenomeDetailPage smart component is called from the route corresponding to genome/:genomeId.
 * The children prop corresponds to the smart component injected from the nested routes (e.g. genome/:genomeId/summary)
 * The GenomePage will take care of filling up the basic genome data and the nested child pages may request additional
 * data that is needed to show the nested page (such as statistics child collections for the summary page)
 */
const GenomeDetailPage: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { genomeId } = useParams<"genomeId">();
  const { isLoading, genome } = useSelector<IAppState, IGenomeState>((state) => state.genome);

  useEffect(() => {
    if (genomeId) {
      dispatch(shouldLoadGenome(Number.parseInt(genomeId, 10)));
    }
    // cleanup of the genome summary store
    return () => {
      dispatch({ type: RESET_GENOME });
    };
  }, [dispatch, genomeId]);

  const getTooltip = (entityName: string) => {
    entityName = entityName.replace(/Count/, "");
    if (!entityName.endsWith("s")) {
      entityName += "s";
    }
    return `${entityName.replace(/^\w/, (c) => c.toUpperCase())} have not been imported or calculated for this genome`;
  };

  const getDecoration = (
    what: keyof Organism | keyof GenomeStatistics | typeof GENOMIC_FEATURE_SYNTHETIC_PROPERTY,
  ): { tooltip: string } => {
    const tooltip = getTooltip(what);
    return { tooltip };
  };

  const getNavItems = (): IRouteNavItemProps[] => {
    const genomeId = genome?.id;
    const contigsDec = getDecoration("contigCount");
    const genesDec = getDecoration("geneCount");
    const transcriptsDec = getDecoration("transcriptsCount");
    const proteinsDec = getDecoration("proteinsCount");
    const genomicFeaturesDec = getDecoration(GENOMIC_FEATURE_SYNTHETIC_PROPERTY);
    const flagsDec = getDecoration("flagCount");
    const variantsDec = {
      disabled:
        isLoading ||
        // the Variants are not available for VCF variant mutant organisms
        (genome?.hasVcfGenomeAnalysis && !genome.isReference) ||
        (genome?.hasGenomeAnalysis && !genome.hasVariants) ||
        (!genome?.hasVcfGenomeAnalysis && !genome?.hasGenomeAnalysis),
      tooltip: getTooltip(genome?.hasVcfGenomeAnalysis ? "variants" : "derived variants"),
    };

    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <GenomeSummaryView />,
        default: true,
        isLoading,
      },
      {
        label: "Contigs",
        disabled: !genome?.hasContigs,
        disabledTooltip: contigsDec.tooltip,
        route: "contigs",
        pageComponent: (
          <GenomeRelatedEntitiesView subtitle="List of Contigs" relatedEntitiesType={ENTITY_TYPE.CONTIG} />
        ),
        isLoading,
      },
      {
        label: "Genes",
        disabled: !genome?.hasGenes,
        disabledTooltip: genesDec.tooltip,
        route: "genes",
        pageComponent: <GenomeRelatedEntitiesView subtitle="List of Genes" relatedEntitiesType={ENTITY_TYPE.GENE} />,
        isLoading,
      },
      {
        label: "Transcripts",
        disabled: !genome?.hasTranscripts,
        disabledTooltip: transcriptsDec.tooltip,
        route: "transcripts",
        pageComponent: (
          <GenomeRelatedEntitiesView subtitle="List of Transcripts" relatedEntitiesType={ENTITY_TYPE.TRANSCRIPT} />
        ),
        isLoading,
      },
      {
        label: "Proteins",
        disabled: !genome?.hasProteins,
        disabledTooltip: proteinsDec.tooltip,
        route: "proteins",
        pageComponent: (
          <GenomeRelatedEntitiesView subtitle="List of Proteins" relatedEntitiesType={ENTITY_TYPE.PROTEIN} />
        ),
        isLoading,
      },
      {
        label: genome?.hasVcfGenomeAnalysis ? "Variants" : "Derived Variants",
        disabled: variantsDec.disabled,
        disabledTooltip: variantsDec.tooltip,
        route: "variants",
        pageComponent: <GenomeVariantsView />,
        isLoading,
      },
      {
        label: "Genomic Features",
        disabled: !(genome?.hasNuFeatures || genome?.hasMarkers),
        disabledTooltip: genomicFeaturesDec.tooltip,
        route: "features",
        pageComponent: (
          <GenomeRelatedGenomicFeaturesView toolbarElement={<GenomeNucleotideFeatureSelector genome={genome} />} />
        ),
        isLoading,
      },
      {
        label: "Coordinate Lists",
        route: "coordinates",
        disabled: !genome?.hasContigs,
        disabledTooltip: "Contig location has not been imported or calculated for this genome",
        pageComponent: (
          <GenomeRelatedEntitiesView
            subtitle="Coordinate Lists"
            relatedEntitiesType={ENTITY_TYPE.COORD_LIST}
            actionButtons={[
              <CoordinateListUploadButton
                showLabel
                key={"coord-list-upload-button"}
                defaultOrganismId={Number(genomeId)}
                onUploadSuceeded={() =>
                  dispatch(
                    triggerTableRefresh(createTableSpecForOrganismParent(ENTITY_TYPE.COORD_LIST, Number(genomeId))),
                  )
                }
              />,
              <HeaderDivider key={"divider"} />,
            ]}
          />
        ),
        isLoading,
      },
      {
        label: "Genome Flags",
        disabled: !genome?.hasFlags,
        disabledTooltip: flagsDec.tooltip,
        route: "flags",
        pageComponent: (
          <GenomeRelatedEntitiesView
            subtitle="List of Genome Flags"
            relatedEntitiesType={ENTITY_TYPE.REGION_FLAG}
            hideCheckboxes={true}
          />
        ),
        isLoading,
      },
      {
        label: "Genome View",
        disabled: !genome?.hasContigs,
        disabledTooltip: "Contig location has not been imported or calculated for this genome",
        route: "genome",
        pageComponent: <GenomeGenomeView />,
        isLoading,
      },
      {
        label: "Synteny View",
        disabled: !isSyntenySet(genome?.sequencingType),
        disabledTooltip: `Synteny View is only available for ${pluralize(SEQUENCING_TYPE.PROTEIN_SYNTENY)} or ${pluralize(SEQUENCING_TYPE.PANGENOME)}`,
        route: `synteny`,
        pageComponent: <GenomeSyntenyView />,
        isLoading,
      },
    ];
  };

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.ORGANISM}
      entityId={Number.parseInt(genomeId, 10)}
      getDisplayName={() => genome?.name}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export { GenomeDetailPage };
